import { useState } from 'react';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import {
  Badge,
  Text,
  Code,
  Stack,
  Group,
  Tooltip,
  ActionIcon,
  CopyButton,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCopy,
  IconCheck,
} from '@tabler/icons-react';
import { formatDateDistance } from '../utils/date';
import { generateColorFromString, type ColorTheme } from '../utils/color';

export interface Job {
  id: string;
  job_type: string;
  status: string;
  queued_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  duration_ms?: number;
  env_id?: string;
  env_name?: string;
  namespace?: string;
  user_id?: string;
  user_display_name?: string;
  correlation_id?: string;
}

interface JobTableProps {
  jobs: Job[];
  showEnvColumn?: boolean;
  showUserColumn?: boolean;
  onNavigate?: (job: Job) => void;
  actionColumn?: (job: Job) => React.ReactNode;
  pageSize?: number;
}

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'queued':
      return 'blue';
    case 'processing':
      return 'yellow';
    case 'completed':
      return 'green';
    case 'failed':
      return 'red';
    default:
      return 'gray';
  }
};

export const getJobTypeTheme = (jobType: string): ColorTheme => {
  const lowerJobType = jobType.toLowerCase();

  // Delete operations get danger theme
  if (lowerJobType.startsWith('delete_')) {
    return 'danger';
  }

  // Create operations get success theme
  if (lowerJobType.startsWith('create_')) {
    return 'success';
  }

  // Health checks, fetch, and get operations get neutral theme
  if (
    lowerJobType.includes('health') ||
    lowerJobType.startsWith('fetch_') ||
    lowerJobType.startsWith('get_') ||
    lowerJobType.startsWith('list_')
  ) {
    return 'neutral';
  }

  // Everything else gets muted theme
  return 'muted';
};

export const getJobTypeColor = (jobType: string): string => {
  return generateColorFromString(jobType, getJobTypeTheme(jobType));
};

export const getJobTypeLabel = (jobType: string): string => {
  return jobType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const calculateDuration = (startedAt: string | null | undefined, completedAt: string | null | undefined): string => {
  if (!startedAt || !completedAt) return 'N/A';
  try {
    const start = new Date(startedAt).getTime();
    const end = new Date(completedAt).getTime();
    const durationMs = end - start;
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  } catch {
    return 'N/A';
  }
};

const calculateQueueWait = (queuedAt: string | null | undefined, startedAt: string | null | undefined): string => {
  if (!queuedAt || !startedAt) return 'N/A';
  try {
    const queued = new Date(queuedAt).getTime();
    const started = new Date(startedAt).getTime();
    const waitMs = started - queued;
    const seconds = Math.floor(waitMs / 1000);
    
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  } catch {
    return 'N/A';
  }
};

export function JobTable({ 
  jobs, 
  showEnvColumn = true, 
  showUserColumn = true,
  onNavigate,
  actionColumn,
  pageSize = 20
}: JobTableProps) {
  const [page, setPage] = useState(1);
  const [expandedRecordIds, setExpandedRecordIds] = useState<string[]>([]);

  // Client-side pagination: slice the records array
  const from = (page - 1) * pageSize;
  const to = from + pageSize;
  const paginatedJobs = jobs.slice(from, to);

  const hasExpandableContent = (job: Job): boolean => {
    return !!(job.error_message || job.correlation_id);
  };

  // Build columns dynamically based on props
  const columns: DataTableColumn<Job>[] = [
    {
      accessor: 'id',
      title: 'Job ID',
      width: 120,
      render: (job) => (
        <Tooltip label={job.id}>
          <Code>{job.id.slice(0, 8)}...</Code>
        </Tooltip>
      ),
    },
    {
      accessor: 'job_type',
      title: 'Type',
      width: 160,
      render: (job) => (
        <Badge color={getJobTypeColor(job.job_type)} size="sm">
          {getJobTypeLabel(job.job_type)}
        </Badge>
      ),
    },
    {
      accessor: 'status',
      title: 'Status',
      width: 100,
      render: (job) => (
        <Badge color={getStatusColor(job.status)} size="sm">
          {job.status}
        </Badge>
      ),
    },
  ];

  if (showEnvColumn) {
    columns.push({
      accessor: 'env_name',
      title: 'Env/DB',
      render: (job) => (
        job.env_name ? (
          onNavigate ? (
            <Text
              size="sm"
              c="blue"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                onNavigate(job);
              }}
            >
              {job.env_name}
            </Text>
          ) : (
            <Text size="sm">{job.env_name}</Text>
          )
        ) : (
          <Text size="sm" c="dimmed">-</Text>
        )
      ),
    });
  }

  if (showUserColumn) {
    columns.push({
      accessor: 'user_display_name',
      title: 'User',
      render: (job) => <Text size="sm">{job.user_display_name || job.user_id}</Text>,
    });
  }

  columns.push(
    {
      accessor: 'queued_at',
      title: 'Queued',
      render: (job) => <Text size="sm">{formatDateDistance(job.queued_at)}</Text>,
    },
    {
      accessor: 'duration',
      title: 'Duration',
      render: (job) => <Text size="sm">{calculateDuration(job.started_at, job.completed_at)}</Text>,
    }
  );

  if (actionColumn) {
    columns.push({
      accessor: 'actions',
      title: 'Actions',
      width: 80,
      render: actionColumn,
    });
  }

  return (
    <DataTable
      withTableBorder
      striped
      highlightOnHover
      records={paginatedJobs}
      columns={columns}
      totalRecords={jobs.length}
      recordsPerPage={pageSize}
      page={page}
      onPageChange={setPage}
      idAccessor="id"
      rowExpansion={{
        allowMultiple: true,
        expanded: {
          recordIds: expandedRecordIds,
          onRecordIdsChange: setExpandedRecordIds,
        },
        collapseProps: {
          transitionDuration: 200,
          transitionTimingFunction: 'ease',
        },
        content: ({ record: job }) => (
          hasExpandableContent(job) ? (
            <Stack gap="sm" p="md" bg="gray.0" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
              {/* Full Job ID */}
              <Group justify="space-between">
                <Text size="xs" fw={500} c="dimmed">Full Job ID:</Text>
                <Group gap="xs">
                  <Code>{job.id}</Code>
                  <CopyButton value={job.id}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? 'Copied' : 'Copy'}>
                        <ActionIcon
                          size="sm"
                          color={copied ? 'teal' : 'gray'}
                          variant="subtle"
                          onClick={copy}
                        >
                          {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Group>
              </Group>

              {/* Correlation ID */}
              {job.correlation_id && (
                <Group justify="space-between">
                  <Text size="xs" fw={500} c="dimmed">Correlation ID:</Text>
                  <Group gap="xs">
                    <Code>{job.correlation_id}</Code>
                    <CopyButton value={job.correlation_id}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? 'Copied' : 'Copy'}>
                          <ActionIcon
                            size="sm"
                            color={copied ? 'teal' : 'gray'}
                            variant="subtle"
                            onClick={copy}
                          >
                            {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                </Group>
              )}

              {/* Timing Details */}
              {job.started_at && (
                <Group justify="space-between">
                  <Text size="xs" fw={500} c="dimmed">Queue Wait:</Text>
                  <Text size="xs">{calculateQueueWait(job.queued_at, job.started_at)}</Text>
                </Group>
              )}
              
              {job.completed_at && job.started_at && (
                <Group justify="space-between">
                  <Text size="xs" fw={500} c="dimmed">Run Time:</Text>
                  <Text size="xs">{calculateDuration(job.started_at, job.completed_at)}</Text>
                </Group>
              )}

              {/* Error Message */}
              {job.error_message && (
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconAlertCircle size={14} color="var(--mantine-color-red-6)" />
                    <Text size="xs" fw={500} c="red">Error Message:</Text>
                  </Group>
                  <Code 
                    block 
                    color="red" 
                    style={{ 
                      fontSize: '0.75rem', 
                      maxHeight: 200, 
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {job.error_message}
                  </Code>
                </Stack>
              )}
            </Stack>
          ) : null
        ),
      }}
    />
  );
}
